<template>
  <div
    class="w-full h-full overflow-y-auto bg-gradient-to-b from-[#1b1136] to-[#241048] px-4 pt-4 pb-10 more-hub"
    :class="closing ? 'hub-exit' : 'hub-enter'"
  >
    <!-- Invite friends — the ₪75 referral reward is for business managers only -->
    <div v-if="canManageOthers" :class="{'more-item': !closing}" :style="stagDelay(0)">
      <ReferralBanner @click="openReferral" />
    </div>

    <!-- Config-driven card grid, grouped by category (role-gated via the menu config) -->
    <div
      v-for="(group, gi) in menuGroups"
      :key="group.category"
      :class="{'more-item': !closing}"
      :style="stagDelay(1 + gi)"
    >
      <div class="text-[11px] font-bold uppercase tracking-widest text-white/40 mt-6 mb-3 px-1">
        {{ categoryLabel(group.category) }}
      </div>
      <!-- Help & account actions are plain rows — utilities (logout, contact),
           not feature pages worth a card. -->
      <div
        v-if="group.category === HELP_CATEGORY"
        class="rounded-2xl border border-white/10 bg-white/[0.035] divide-y divide-white/10 overflow-hidden"
      >
        <button
          v-for="item in group.items"
          :key="item.name"
          type="button"
          class="w-full flex items-center gap-3 px-4 py-3.5 text-right transition-colors active:bg-white/[0.08]"
          @click="handleClick(item)"
        >
          <i :class="['pi', item.icon, 'text-base flex-none', item.customAction === 'logout' ? 'text-red-300' : 'text-white/60']" />
          <span :class="['flex-1 text-[14px]', item.customAction === 'logout' ? 'text-red-300' : 'text-white']">{{ item.title }}</span>
          <i v-if="!item.customAction" class="pi pi-chevron-left text-[11px] text-white/30 flex-none" />
        </button>
      </div>

      <div v-else class="grid grid-cols-2 gap-3">
        <button
          v-for="item in group.items"
          :key="item.name"
          type="button"
          class="relative flex flex-col text-right rounded-2xl border border-white/10 bg-white/[0.035] p-4 min-h-[150px] transition-colors active:bg-white/[0.08]"
          @click="handleClick(item)"
        >
          <span
            v-if="countBadge(item.name)"
            class="absolute top-3 left-3 min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[11px] font-bold text-white bg-red-500 rounded-full"
          >{{ countBadge(item.name) }}</span>
          <span
            v-else-if="badges[item.name]"
            class="absolute top-3 left-3 text-[9px] font-semibold text-purple-200 bg-primary-500/25 px-2 py-0.5 rounded-full"
          >{{ badges[item.name] }}</span>

          <span class="self-start w-12 h-12 rounded-xl flex items-center justify-center" :style="iconColor(item.name)">
            <i :class="['pi', item.icon, 'text-lg']" />
          </span>

          <div class="mt-auto pt-6">
            <div class="text-[15px] font-semibold text-white leading-tight">{{ item.title }}</div>
            <div v-if="descriptions[item.name]" class="text-[12px] text-white/45 mt-1 leading-snug">{{ descriptions[item.name] }}</div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAdminAutomations, useAdminStore, useAdminTasksStore } from '@app/stores';
import { adminPageConfig, type IAdminPageConfig } from '@app/types/admin-page-config';
import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';
import ReferralBanner from '@app/components/admin/components/ReferralBanner.vue';

const emit = defineEmits<{ 'parent-function': [method: string] }>();

const router = useRouter();
const adminStore = useAdminStore();
const tasksStore = useAdminTasksStore();
const automationsStore = useAdminAutomations();

/** Red count on a card: due tasks, or today's smart-nudge suggestions on Automations. */
const countBadge = (name: string): number => {
  if (name === AdminRouteNames.Tasks) return tasksStore.getDueNowCount;
  if (name === AdminRouteNames.Communication) return automationsStore.nudgeCount;
  return 0;
};
adminStore.setPage(AdminRouteNames.More);

const canManageOthers = computed(() => adminStore.getCanManageOthers);
const hasPaymentsAccess = computed(() => {
  const integration = adminStore.getBusinessSiteData?.paymentIntegration;
  return !!integration?.paymentConfig?.provider || !!integration?.invoiceConfig?.provider;
});

// ── Entrance / exit choreography ─────────────────────────────────────────────
// The hub slides+fades in; a `closing` state plays a 230ms exit BEFORE the real
// navigation fires, so taps feel deliberate instead of jumpy.
const EXIT_MS = 230;
const closing = ref(false);
const dismiss = (fn: () => void) => {
  if (closing.value) return;
  closing.value = true;
  setTimeout(fn, EXIT_MS);
};
// each content block enters staggered (80 + i*45ms), unless we're closing
const stagDelay = (i: number) => (closing.value ? {} : { animationDelay: `${80 + i * 45}ms` });

// Pages that already live in the bottom nav are not repeated on the More page.
const BOTTOM_NAV = new Set<string>([AdminRouteNames.Home, AdminRouteNames.Clients, AdminRouteNames.Team]);

const menuGroups = computed(() => {
  const items = adminPageConfig.filter(p => {
    if (!p.isMenuItem) return false;
    if (BOTTOM_NAV.has(p.name)) return false;
    if (p.onlyAdmin && !canManageOthers.value) return false; // employee apps see a reduced set
    if (p.requiresPaymentsAccess && !hasPaymentsAccess.value) return false;
    return true;
  });
  const byCat: Record<string, IAdminPageConfig[]> = {};
  const order: string[] = [];
  for (const item of items) {
    const cat = item.category || '';
    if (!byCat[cat]) { byCat[cat] = []; order.push(cat); }
    byCat[cat].push(item);
  }
  return order.map(category => ({ category, items: byCat[category] }));
});

const categoryLabel = (cat: string) => (cat === 'שיווק' ? 'שיווק וצמיחה' : cat);

// This category renders as plain rows instead of feature cards.
const HELP_CATEGORY = 'עזרה';

const descriptions: Record<string, string> = {
  [AdminRouteNames.Tasks]: 'מה צריך לעשות היום — לבד או עם הצוות',
  [AdminRouteNames.BusinessConfigurations]: 'תפריט הטיפולים, מחירים ומשכי זמן',
  [AdminRouteNames.ConsentForms]: 'טפסי הסכמה לחתימה דיגיטלית וקבצים',
  [AdminRouteNames.Vouchers]: 'מכרו חבילות מראש והחזירו לקוחות',
  [AdminRouteNames.Communication]: 'תזכורות ואישורים שנשלחים לבד — 24/7',
  [AdminRouteNames.Campaigns]: 'שליחת SMS יזום לקבוצות לקוחות',
  [AdminRouteNames.Shop]: 'חנות מוצרים ומכירה בקופה',
  [AdminRouteNames.LoyaltyProgram]: 'הטבות שמייצרות נאמנות',
  [AdminRouteNames.Analytics]: 'הכנסות, ביצועים ומגמות',
  [AdminRouteNames.Payments]: 'מה נסלק והעברות לחשבון הבנק',
  [AdminRouteNames.Configurations]: 'עסק, תשלומים, משתמשים וחיבורים',
  [AdminRouteNames.Integrations]: 'חיבור לתשלומים, חשבוניות וכלים נוספים',
};

const badges: Record<string, string> = {
  [AdminRouteNames.Communication]: 'חוסך זמן',
};

// Soft colored icon tiles (translucent on the dark background).
const ICON_COLORS: Record<string, { background: string; color: string }> = {
  [AdminRouteNames.Tasks]: { background: 'rgba(251,146,60,.16)', color: '#fdba74' },
  [AdminRouteNames.ConsentForms]: { background: 'rgba(96,165,250,.16)', color: '#93c5fd' },
  [AdminRouteNames.BusinessConfigurations]: { background: 'rgba(167,139,250,.16)', color: '#c4b5fd' },
  [AdminRouteNames.Vouchers]: { background: 'rgba(99,102,241,.16)', color: '#a5b4fc' },
  [AdminRouteNames.Communication]: { background: 'rgba(250,204,21,.16)', color: '#fde047' },
  [AdminRouteNames.Campaigns]: { background: 'rgba(244,114,182,.16)', color: '#f9a8d4' },
  [AdminRouteNames.Shop]: { background: 'rgba(148,163,184,.16)', color: '#cbd5e1' },
  [AdminRouteNames.LoyaltyProgram]: { background: 'rgba(52,211,153,.16)', color: '#6ee7b7' },
  [AdminRouteNames.Analytics]: { background: 'rgba(167,139,250,.16)', color: '#c4b5fd' },
  [AdminRouteNames.Payments]: { background: 'rgba(134,239,172,.16)', color: '#86efac' },
  [AdminRouteNames.Configurations]: { background: 'rgba(148,163,184,.16)', color: '#cbd5e1' },
  [AdminRouteNames.Integrations]: { background: 'rgba(45,212,191,.16)', color: '#5eead4' },
};
const iconColor = (name: string) => ICON_COLORS[name] ?? { background: 'rgba(255,255,255,.08)', color: '#c4b5fd' };

const openReferral = () => {
  trackEvent('referral_invite_clicked', { source: 'more_page' });
  dismiss(() => {
    router.push({ name: AdminRouteNames.ReferralProgram });
    adminStore.setPage(AdminRouteNames.ReferralProgram);
  });
};

const handleClick = (item: IAdminPageConfig) => {
  // utility actions (logout / WhatsApp) open outside the hub — no exit dance
  if (item.customAction === 'logout') { emit('parent-function', 'logout'); return; }
  if (item.customAction === 'contact-us') { emit('parent-function', 'contact-us'); return; }
  trackEvent('more_menu_item_clicked', { page: item.name });
  dismiss(() => {
    router.push({ name: item.name as AdminRouteNames });
    adminStore.setPage(item.name as AdminRouteNames);
  });
};
</script>

<style scoped lang="scss">
.hub-enter {
  animation: more-hub-in 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.hub-exit {
  animation: more-hub-out 0.23s cubic-bezier(0.4, 0, 1, 1) both;
}

.more-item {
  animation: more-hub-item 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes more-hub-in {
  from { opacity: 0; transform: translateY(6%); }
  to { opacity: 1; transform: none; }
}

@keyframes more-hub-out {
  from { opacity: 1; transform: none; }
  to { opacity: 0; transform: translateY(5%); }
}

@keyframes more-hub-item {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
}
</style>
